import { AlertTriangle, Edit2, Loader2, Plus, Trash2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useApplySchedule, useAssignmentsByProgramme } from "@/hooks/admin/assignments";
import { APP_TIME_ZONE, appWallTimeToUtc, formatZonedTime, zonedDateKey } from "@/lib/appTime";
import { api } from "@/services/api";
import type { Programme, ScheduleWindowInput } from "@/types";
import { numberWindows } from "@/utils/schedule";
import { AssignmentForm } from "./AssignmentForm";
import { SchedulePreview } from "./SchedulePreview";

/**
 * Cohort-level "Manage schedule" surface (KC-375 / KC-378).
 *
 * The hybrid per-window editor: admins add, edit, delete, and re-date assignment
 * windows; week / assignment / work-package numbers are derived and shown
 * read-only. Saving replaces the whole window set via the bulk schedule
 * endpoint. On a draft or zero-enrollment cohort any admin can save freely; on
 * an enrolled cohort a destructive shrink (removing windows that hold
 * submissions/reviews) first previews the blast radius and requires a typed
 * DELETE confirmation, and the whole change is superadmin-only.
 */

interface ScheduleModalProps {
  programme: Programme;
  open: boolean;
  onClose: () => void;
}

interface Row {
  key: string;
  id?: string; // existing assignment id (undefined = new window)
  start_date: string; // YYYY-MM-DD (app-timezone calendar date)
  deadline: string; // YYYY-MM-DD (app-timezone calendar date)
  start_time: string; // HH:MM (app-timezone wall-clock)
  deadline_time: string; // HH:MM (app-timezone wall-clock)
}

let rowSeq = 0;
function newKey(): string {
  rowSeq += 1;
  return `row-${rowSeq}`;
}

// The app-timezone open/close time to seed *new* windows with, taken from the
// cohort's existing windows (they share one time) — noon as a last resort.
function defaultTime(rows: Row[]): string {
  return rows[0]?.start_time ?? "12:00";
}

function toWindow(row: Row): { start: Date; deadline: Date } {
  return {
    start: appWallTimeToUtc(row.start_date, row.start_time),
    deadline: appWallTimeToUtc(row.deadline, row.deadline_time),
  };
}

/** Client-side mirror of the backend validation for immediate feedback. */
function validateRows(rows: Row[]): string | null {
  if (rows.length === 0) return "A cohort needs at least one assignment.";
  const windows = rows.map(toWindow).sort((a, b) => a.start.getTime() - b.start.getTime());
  let prevDeadline = -Infinity;
  for (const w of windows) {
    if (w.deadline <= w.start) return "Each assignment's deadline must be after its start.";
    if (w.start.getTime() < prevDeadline) return "Assignment windows must not overlap.";
    prevDeadline = w.deadline.getTime();
  }
  return null;
}

function statusBadge(status: string | undefined) {
  const config: Record<string, { label: string; tone: BadgeTone }> = {
    locked: { label: "LOCKED", tone: "neutral" },
    active: { label: "ACTIVE", tone: "success" },
    closed: { label: "CLOSED", tone: "warning" },
  };
  const { label, tone } = config[status ?? ""] ?? { label: "—", tone: "neutral" };
  return <StatusBadge tone={tone}>{label}</StatusBadge>;
}

export function ScheduleModal({ programme, open, onClose }: ScheduleModalProps) {
  const assignmentsQuery = useAssignmentsByProgramme(programme.id, open);
  const applySchedule = useApplySchedule();

  const [rows, setRows] = useState<Row[]>([]);
  const [editingRow, setEditingRow] = useState<Row | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmText, setConfirmText] = useState("");
  // When a destructive shrink is detected we hold the pending windows + preview
  // counts until the admin types DELETE.
  const [pendingDestroy, setPendingDestroy] = useState<{
    windows: ScheduleWindowInput[];
    submissions: number;
    reviews: number;
    windowsRemoved: number;
  } | null>(null);

  const enrollmentCount = programme.enrollment_count ?? 0;
  const enrolled = enrollmentCount > 0 && !programme.is_draft;

  // Seed local editable rows from the server once the data loads / dialog opens.
  const assignments = assignmentsQuery.data;
  useEffect(() => {
    if (!open) return;
    if (!assignments) return;
    setRows(
      assignments.map((a) => {
        const start = new Date(a.start_date);
        const deadline = new Date(a.deadline);
        return {
          key: newKey(),
          id: a.id,
          start_date: zonedDateKey(start, APP_TIME_ZONE),
          deadline: zonedDateKey(deadline, APP_TIME_ZONE),
          start_time: formatZonedTime(start, APP_TIME_ZONE),
          deadline_time: formatZonedTime(deadline, APP_TIME_ZONE),
        };
      })
    );
    setError(null);
    setPendingDestroy(null);
    setConfirmText("");
  }, [open, assignments]);

  // Time-status per existing assignment id (for the read-only status column).
  const statusById = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const a of assignments ?? []) map.set(a.id, a.time_status);
    return map;
  }, [assignments]);

  // Derive week / assignment / WP numbers for display (chronological).
  const display = useMemo(() => {
    const sorted = [...rows].sort(
      (a, b) => toWindow(a).start.getTime() - toWindow(b).start.getTime()
    );
    const numbered = numberWindows(sorted.map(toWindow));
    return sorted.map((row, i) => ({ row, ...numbered[i] }));
  }, [rows]);

  const validationError = validateRows(rows);

  const buildWindows = (): ScheduleWindowInput[] =>
    rows.map((r) => {
      const w = toWindow(r);
      return {
        id: r.id ?? null,
        start_date: w.start.toISOString(),
        deadline: w.deadline.toISOString(),
      };
    });

  const runApply = async (windows: ScheduleWindowInput[], confirm?: string) => {
    setError(null);
    try {
      await applySchedule.mutateAsync({ programmeId: programme.id, windows, confirm });
      setPendingDestroy(null);
      onClose();
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 403) {
        setError("Restructuring an enrolled cohort's schedule is restricted to superadmins.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to save schedule");
      }
    }
  };

  const handleSave = async () => {
    if (validationError) {
      setError(validationError);
      return;
    }
    const windows = buildWindows();
    setError(null);
    try {
      const preview = await api.previewSchedule(programme.id, windows);
      if (preview.destructive) {
        setConfirmText("");
        setPendingDestroy({
          windows,
          submissions: preview.submissions_to_delete,
          reviews: preview.reviews_to_delete,
          windowsRemoved: preview.windows_removed,
        });
        return;
      }
      await runApply(windows);
    } catch (err) {
      const status = (err as { status?: number }).status;
      if (status === 403) {
        setError("Restructuring an enrolled cohort's schedule is restricted to superadmins.");
      } else {
        setError(err instanceof Error ? err.message : "Failed to preview schedule");
      }
    }
  };

  const isSaving = applySchedule.isPending;

  return (
    <>
      <Dialog open={open && !showAddForm && !editingRow} onOpenChange={onClose}>
        <DialogContent className="max-h-[90vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-gray-900">Manage schedule</DialogTitle>
            <p className="mt-1 text-sm text-gray-500">
              {programme.name} · {rows.length} {rows.length === 1 ? "assignment" : "assignments"}
            </p>
          </DialogHeader>

          {enrolled && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0" />
              <span>
                This cohort has {enrollmentCount} enrolled{" "}
                {enrollmentCount === 1 ? "participant" : "participants"}. Structural changes are
                superadmin-only, and removing windows that hold submissions requires confirmation.
              </span>
            </div>
          )}

          <div className="space-y-4">
            {display.length > 0 && (
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <SchedulePreview rows={display} />
              </div>
            )}

            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => setShowAddForm(true)}
                className="flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary/90"
              >
                <Plus className="h-4 w-4" />
                Add window
              </button>
            </div>

            {assignmentsQuery.isPending && open ? (
              <div className="flex justify-center py-10">
                <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <div className="overflow-hidden rounded-xl border border-gray-200">
                <div className="grid grid-cols-[3rem_2rem_3rem_1fr_1fr_5rem_4rem] gap-x-3 bg-gray-50 px-4 py-2 text-xs font-medium text-gray-500">
                  <span>Week</span>
                  <span>#</span>
                  <span>WP</span>
                  <span>Start</span>
                  <span>Deadline</span>
                  <span>Status</span>
                  <span className="text-right">Actions</span>
                </div>
                <div className="divide-y divide-gray-100">
                  {display.length === 0 ? (
                    <div className="px-4 py-6 text-center text-sm text-gray-400">
                      No windows yet — add one to build the schedule.
                    </div>
                  ) : (
                    display.map(({ row, week, assignment, wp }) => (
                      <div
                        key={row.key}
                        className="grid grid-cols-[3rem_2rem_3rem_1fr_1fr_5rem_4rem] items-center gap-x-3 px-4 py-2.5 text-sm text-gray-800"
                      >
                        <span className="font-medium">W{week}</span>
                        <span className="text-gray-500">{assignment}</span>
                        <span className="text-gray-500">{wp}</span>
                        <span className="text-gray-600">{row.start_date}</span>
                        <span className="text-gray-600">{row.deadline}</span>
                        <span>
                          {row.id ? statusBadge(statusById.get(row.id)) : statusBadge("locked")}
                        </span>
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            type="button"
                            onClick={() => setEditingRow(row)}
                            className="rounded p-1.5 text-gray-600 transition-colors hover:bg-gray-200"
                            title="Edit"
                          >
                            <Edit2 className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setRows((rs) => rs.filter((r) => r.key !== row.key))}
                            className="rounded p-1.5 text-gray-600 transition-colors hover:bg-red-100 hover:text-red-600"
                            title="Remove"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {(error || validationError) && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">
                {error ?? validationError}
              </div>
            )}

            <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={onClose}
                disabled={isSaving}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving || !!validationError}
                className="flex items-center gap-2 rounded-lg bg-primary px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Save schedule
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add / edit a single window (local state only; persisted on Save) */}
      {showAddForm && (
        <AssignmentForm
          onSave={(w) =>
            setRows((rs) => [
              ...rs,
              {
                key: newKey(),
                start_date: w.start_date,
                deadline: w.deadline,
                start_time: defaultTime(rs),
                deadline_time: defaultTime(rs),
              },
            ])
          }
          onClose={() => setShowAddForm(false)}
        />
      )}
      {editingRow && (
        <AssignmentForm
          window={{ start_date: editingRow.start_date, deadline: editingRow.deadline }}
          onSave={(w) =>
            setRows((rs) =>
              rs.map((r) =>
                r.key === editingRow.key
                  ? { ...r, start_date: w.start_date, deadline: w.deadline }
                  : r
              )
            )
          }
          onClose={() => setEditingRow(null)}
        />
      )}

      {/* Destructive-shrink typed confirmation (KC-378) */}
      {pendingDestroy && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            <div className="mb-4 flex items-start gap-3">
              <div className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Delete participant data?</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Removing {pendingDestroy.windowsRemoved}{" "}
                  {pendingDestroy.windowsRemoved === 1 ? "window" : "windows"} will permanently
                  delete <strong>{pendingDestroy.submissions}</strong>{" "}
                  {pendingDestroy.submissions === 1 ? "submission" : "submissions"} and{" "}
                  <strong>{pendingDestroy.reviews}</strong>{" "}
                  {pendingDestroy.reviews === 1 ? "review" : "reviews"}. This cannot be undone.
                </p>
              </div>
            </div>
            <label
              className="mb-1 block text-sm font-medium text-gray-700"
              htmlFor="confirm-delete"
            >
              Type <span className="font-mono font-bold">DELETE</span> to confirm
            </label>
            <input
              id="confirm-delete"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="mb-4 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm outline-none focus:border-red-500 focus:ring-1 focus:ring-red-500"
              placeholder="DELETE"
            />
            {error && <p className="mb-3 text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setPendingDestroy(null)}
                disabled={isSaving}
                className="rounded-lg px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => runApply(pendingDestroy.windows, "DELETE")}
                disabled={isSaving || confirmText !== "DELETE"}
                className="flex items-center gap-2 rounded-lg bg-red-600 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
              >
                {isSaving && <Loader2 className="h-4 w-4 animate-spin" />}
                Delete and save
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
